import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../lib/supabaseClient'
import { useUser } from '../hooks/useUser'
import '../styles/SettingsModal.css'

// ── Validation (mirror of AuthPage) ──
function validateUsername(username) {
  if (!username || username.trim().length === 0) return 'Username is required.'
  if (username.trim().length < 3)  return 'Username must be at least 3 characters.'
  if (username.trim().length > 10) return 'Username must be 10 characters or fewer.'
  if (!/^[A-Za-z0-9]+$/.test(username.trim())) return 'Only letters and numbers — no spaces or special characters.'
  return null
}

function SunIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  )
}

function MoonIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  )
}

export default function SettingsModal({ onClose }) {
  const { profile, logout, user, refreshProfile } = useUser()

  // ── Theme ──
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('azerodle-theme')
    return saved || document.documentElement.dataset.theme || 'dark'
  })

  const applyTheme = (t) => {
    document.documentElement.dataset.theme = t
    localStorage.setItem('azerodle-theme', t)
    setTheme(t)
  }

  // ── Change email ──
  const [newEmail, setNewEmail] = useState('')
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailMsg, setEmailMsg] = useState(null) // { type: 'ok'|'err', text }

  const handleEmailChange = async () => {
    if (!newEmail.trim()) return setEmailMsg({ type: 'err', text: 'Enter a new email.' })
    setEmailLoading(true); setEmailMsg(null)
    const { error } = await supabase.auth.updateUser({ email: newEmail.trim() })
    setEmailLoading(false)
    if (error) return setEmailMsg({ type: 'err', text: error.message })
    setEmailMsg({ type: 'ok', text: 'Confirmation sent to new email.' })
    setNewEmail('')
  }

  // ── Change username ──
  const [newUsername, setNewUsername]       = useState('')
  const [usernameTouched, setUsernameTouched] = useState(false)
  const [usernameLoading, setUsernameLoading] = useState(false)
  const [usernameMsg, setUsernameMsg]         = useState(null)
  const usernameError = usernameTouched ? validateUsername(newUsername) : null

  // Cooldown: read last_username_change from profile
  const lastChange = profile?.last_username_change ? new Date(profile.last_username_change) : null
  const cooldownMs = 90 * 24 * 60 * 60 * 1000 // 3 months
  const nextAllowed = lastChange ? new Date(lastChange.getTime() + cooldownMs) : null
  const canChangeName = !nextAllowed || new Date() >= nextAllowed

  const handleUsernameChange = async () => {
    setUsernameTouched(true)
    const err = validateUsername(newUsername)
    if (err) return setUsernameMsg({ type: 'err', text: err })
    if (!canChangeName) return

    setUsernameLoading(true); setUsernameMsg(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) return setUsernameMsg({ type: 'err', text: 'Not authenticated.' })

      const res = await fetch('/api/users/update-username', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ username: newUsername.trim() })
      })
      const d = await res.json()
      if (!res.ok) return setUsernameMsg({ type: 'err', text: d.error || 'Failed to update.' })
      setUsernameMsg({ type: 'ok', text: 'Username updated!' })
      setNewUsername('')
      setUsernameTouched(false)
      await refreshProfile()
    } catch {
      setUsernameMsg({ type: 'err', text: 'Could not connect to server.' })
    } finally {
      setUsernameLoading(false)
    }
  }

  // ── Delete account ──
  const [deletePhase, setDeletePhase] = useState('idle') // idle | confirm | sending | otp | verifying | deleting
  const [deleteMsg, setDeleteMsg]     = useState(null)
  const [otpCode, setOtpCode]         = useState('')

  const handleRequestDeleteOtp = async () => {
    setDeletePhase('sending'); setDeleteMsg(null)
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: user.email,
        options: { shouldCreateUser: false }
      })
      if (error) {
        setDeletePhase('confirm')
        return setDeleteMsg({ type: 'err', text: error.message })
      }
      setDeletePhase('otp')
    } catch {
      setDeletePhase('confirm')
      setDeleteMsg({ type: 'err', text: 'Could not send confirmation email.' })
    }
  }

  const handleDeleteAccount = async () => {
    if (!otpCode.trim()) return setDeleteMsg({ type: 'err', text: 'Enter the code from your email.' })
    setDeletePhase('verifying'); setDeleteMsg(null)

    // ── Verify OTP ──
    const { error: otpError } = await supabase.auth.verifyOtp({
      email: user.email,
      token: otpCode.trim(),
      type:  'email',
    })
    if (otpError) {
      setDeletePhase('otp')
      return setDeleteMsg({ type: 'err', text: 'Invalid or expired code. Try again.' })
    }

    // ── Delete account ──
    setDeletePhase('deleting')
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) {
        setDeletePhase('otp')
        return setDeleteMsg({ type: 'err', text: 'Not authenticated.' })
      }

      const res = await fetch('/api/users/delete', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (!res.ok) {
        const d = await res.json()
        setDeletePhase('otp')
        return setDeleteMsg({ type: 'err', text: d.error || 'Failed to delete account.' })
      }
      await supabase.auth.signOut()
      logout()
      onClose()
    } catch {
      setDeletePhase('otp')
      setDeleteMsg({ type: 'err', text: 'Could not connect to server.' })
    }
  }

  // ── Escape + body lock ──
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handler)
    }
  }, [onClose])

  const formatDate = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })

  return createPortal(
    <div className="settings-backdrop">
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <div className="settings-header">
          <h2 className="settings-title">Settings</h2>
          <button className="settings-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── APPEARANCE ── */}
        <section className="settings-section">
          <h3 className="settings-section-title">Appearance</h3>
          <div className="theme-toggle-row">
            <span className="settings-label">Theme</span>
            <div className="theme-toggle">
              <button
                className={`theme-btn ${theme === 'light' ? 'active' : ''}`}
                onClick={() => applyTheme('light')}
                aria-label="Light mode"
              >
                <SunIcon /> Light
              </button>
              <button
                className={`theme-btn ${theme === 'dark' ? 'active' : ''}`}
                onClick={() => applyTheme('dark')}
                aria-label="Dark mode"
              >
                <MoonIcon /> Dark
              </button>
            </div>
          </div>
        </section>

        {/* ── CHANGE EMAIL ── */}
        <section className="settings-section">
          <h3 className="settings-section-title">Change Email</h3>
          <p className="settings-hint">Current: <strong>{user?.email || '—'}</strong></p>
          <div className="settings-field-row">
            <input
              className="settings-input"
              type="email"
              placeholder="new@email.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleEmailChange()}
            />
            <button className="settings-btn-gold" onClick={handleEmailChange} disabled={emailLoading}>
              {emailLoading ? '...' : 'Update'}
            </button>
          </div>
          {emailMsg && (
            <p className={`settings-msg ${emailMsg.type}`}>{emailMsg.type === 'ok' ? '✔' : '⚠'} {emailMsg.text}</p>
          )}
        </section>

        {/* ── CHANGE USERNAME ── */}
        <section className="settings-section">
          <h3 className="settings-section-title">Change Name</h3>
          <p className="settings-hint">Current: <strong>{profile?.username || '—'}</strong></p>
          {!canChangeName && nextAllowed && (
            <p className="settings-cooldown">
              🕐 Next change available: <strong>{formatDate(nextAllowed)}</strong>
            </p>
          )}
          <div className="settings-field-row">
            <div className="settings-input-wrap">
              <input
                className={`settings-input ${usernameError ? 'input-error' : newUsername && !usernameError && usernameTouched ? 'input-ok' : ''}`}
                type="text"
                placeholder="NewNick"
                maxLength={10}
                value={newUsername}
                disabled={!canChangeName}
                onChange={e => setNewUsername(e.target.value)}
                onBlur={() => setUsernameTouched(true)}
                onKeyDown={e => e.key === 'Enter' && handleUsernameChange()}
              />
              {usernameError && <span className="settings-field-error">{usernameError}</span>}
            </div>
            <button
              className="settings-btn-gold"
              onClick={handleUsernameChange}
              disabled={usernameLoading || !canChangeName}
            >
              {usernameLoading ? '...' : 'Update'}
            </button>
          </div>
          {usernameMsg && (
            <p className={`settings-msg ${usernameMsg.type}`}>{usernameMsg.type === 'ok' ? '✔' : '⚠'} {usernameMsg.text}</p>
          )}
          <p className="settings-fine-print">Can be changed once every 3 months.</p>
        </section>

        {/* ── DELETE ACCOUNT ── */}
        <section className="settings-section settings-section-danger">
          <h3 className="settings-section-title danger">Delete Account</h3>
          <p className="settings-hint">This permanently removes your account and all data. There is no undo.</p>

          {deletePhase === 'idle' && (
            <button className="settings-btn-danger" onClick={() => setDeletePhase('confirm')}>
              Delete my account
            </button>
          )}

          {deletePhase === 'confirm' && (
            <div className="settings-delete-confirm">
              <p className="settings-delete-warn">Are you absolutely sure?</p>
              <div className="settings-delete-actions">
                <button className="settings-btn-danger" onClick={handleRequestDeleteOtp}>
                  Yes, send confirmation
                </button>
                <button className="settings-btn-ghost" onClick={() => { setDeletePhase('idle'); setDeleteMsg(null) }}>
                  Cancel
                </button>
              </div>
              {deleteMsg && <p className={`settings-msg ${deleteMsg.type}`}>⚠ {deleteMsg.text}</p>}
            </div>
          )}

          {deletePhase === 'sending' && (
            <p className="settings-hint">Sending confirmation code to <strong>{user?.email}</strong>...</p>
          )}

          {(deletePhase === 'otp' || deletePhase === 'verifying' || deletePhase === 'deleting') && (
            <div className="settings-delete-confirm">
              <p className="settings-hint">
                Enter the 8-digit code sent to <strong>{user?.email}</strong>
              </p>
              <div className="settings-field-row">
                <input
                  className="settings-input"
                  type="text"
                  placeholder="00000000"
                  maxLength={8}
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={e => e.key === 'Enter' && handleDeleteAccount()}
                  disabled={deletePhase === 'verifying' || deletePhase === 'deleting'}
                />
                <button
                  className="settings-btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={deletePhase === 'verifying' || deletePhase === 'deleting'}
                >
                  {deletePhase === 'verifying' || deletePhase === 'deleting' ? '...' : 'Confirm'}
                </button>
              </div>
              <div className="settings-delete-actions" style={{ marginTop: '0.5rem' }}>
                <button
                  className="settings-btn-ghost"
                  onClick={() => { setDeletePhase('idle'); setDeleteMsg(null); setOtpCode('') }}
                  disabled={deletePhase === 'verifying' || deletePhase === 'deleting'}
                >
                  Cancel
                </button>
                <button
                  className="settings-btn-ghost"
                  onClick={handleRequestDeleteOtp}
                  disabled={deletePhase === 'verifying' || deletePhase === 'deleting'}
                >
                  Resend code
                </button>
              </div>
              {deleteMsg && <p className={`settings-msg ${deleteMsg.type}`}>⚠ {deleteMsg.text}</p>}
            </div>
          )}
        </section>
      </div>
    </div>,
    document.body
  )
}